import Autobase from 'autobase'
import Hyperbee from 'hyperbee'
import b4a from 'b4a'
import z32 from 'z32'
import { randomBytes, domainHash } from '@pine/crypto'
import {
  OP, KIND, ROLE, ROOM_TYPE, DELETE_SCOPE, CALL_STATE,
  createOp, encodeOp, createApply, RoomView, can, ACTION, normalizeSettings
} from '@pine/protocol'
import { Emitter } from './emitter.js'
import { BlobStore } from './blobs.js'
import { EphemeralBus, EPH } from './ephemeral.js'
import { now as clockNow } from './clock.js'

const TYPING_TTL = 4000
// How far ahead of our own clock an ordinary message is stamped to keep it
// after the newest one in the room (see _nextTimestamp)...
const CLOCK_FOLLOW_MAX = 3 * 1000
// ...and how far an answer may follow the message it answers (send's `after`).
const ANSWER_FOLLOW_MAX = 10 * 60 * 1000
const PRESENCE_INTERVAL = 20000

/**
 * A room is one Autobase.
 *
 * Every member writes to their own Hypercore; Autobase linearises those logs
 * into a single ordered sequence and `apply` folds that sequence into a
 * Hyperbee that holds messages, members, reactions and read state. Because
 * apply is deterministic, two peers that have seen the same logs always agree
 * on the room without ever asking a server what the truth is.
 *
 * Group chats, one-to-one chats and broadcast channels are all this same
 * object — only ROOM_TYPE and the permission matrix differ.
 */
export class Room extends Emitter {
  constructor (client, { key = null, encryptionKey = null, localId = null } = {}) {
    super()
    this.client = client
    this.key = key ? toBuf(key) : null
    this.encryptionKey = encryptionKey ? toBuf(encryptionKey) : null
    this.localId = localId || (key ? z32.encode(toBuf(key)) : null)

    this.base = null
    this.view = null
    this.blobs = null
    this.bus = null
    this.discovery = null

    this.opening = null
    this.closing = null
    this.closed = false

    this.typing = new Map()    // account -> expiry
    this.presence = new Map()  // account -> { state, at }
    this.seen = new Map()      // account -> when anything was last heard from them
    this._typingTimer = null
    this._presenceTimer = null
    this._detach = null
    this._lastMeta = null

    // Asking the room to let this device write (seekAdmission), and what
    // was written here meanwhile (the outbox).
    this.wantsAdmission = false
    this._admissionTimer = null
    this._admitted = new Map() // writer hex -> when we last let it in
    this.outbox = []
    this._outboxWaiters = new Map()
    this._flushing = false
  }

  /* ------------------------------------------------------------------ */
  /* lifecycle                                                          */
  /* ------------------------------------------------------------------ */

  ready () {
    if (!this.opening) this.opening = this._open()
    return this.opening
  }

  /**
   * Where this room's cores live on disk.
   *
   * It has to be stable across restarts *and* knowable before Autobase exists,
   * because the room key is only assigned once the base is open. The room's
   * encryption key is the one identifier every path — create, join, reopen —
   * already holds up front, so the namespace hangs off that.
   */
  get namespace () {
    return 'room/' + z32.encode(domainHash('pine/room-namespace/v1', this.encryptionKey))
  }

  async _open () {
    if (!this.encryptionKey) throw new Error('A room needs an encryption key before it can be opened')
    const store = this.client.transport.store.namespace(this.namespace)

    this.base = new Autobase(store, this.key, {
      encrypt: true,
      encryptionKey: this.encryptionKey,
      valueEncoding: 'binary',
      ackInterval: 1000,
      open: (viewStore) => new Hyperbee(viewStore.get('pine'), {
        keyEncoding: 'utf-8',
        valueEncoding: 'json',
        extension: false
      }),
      apply: createApply(),
      close: (view) => view.close()
    })

    await this.base.ready()

    this.key = this.base.key
    this.encryptionKey = this.base.encryptionKey
    this.localId = z32.encode(this.key)
    this.view = new RoomView(this.base.view)

    this.blobs = new BlobStore(store, { encryptionKey: this.encryptionKey })
    await this.blobs.ready()

    this.base.on('update', () => this._onUpdate())
    this.base.on('error', (err) => this.emit('error', err))

    this.bus = new EphemeralBus({
      identity: this.client.identity,
      topic: this.base.discoveryKey,
      onMessage: (msg) => this._onEphemeral(msg),
      onPeer: (remoteId) => {
        if (!this.bus) return
        this.bus.send(EPH.PRESENCE, { state: 'online' }, { to: remoteId })
        // Somebody who may be able to let this device in has just arrived.
        if (this.wantsAdmission && !this.writable) this.requestAdmission({ to: remoteId })
        // Who we are, to whoever just arrived — without the photo, which
        // travels only when it changes. A bot's commands reach a chat it
        // has just joined this way, rather than at its next profile edit.
        this.announceProfile({ withAvatar: false, to: remoteId })
      }
    })

    this._detach = this.client.transport.onConnection((conn, info) => this.bus.attach(conn, info))
    for (const conn of this.client.transport.connections) this.bus.attach(conn)

    this.discovery = this.client.transport.join(this.base.discoveryKey)

    this._presenceTimer = setInterval(() => this.announcePresence('online'), PRESENCE_INTERVAL)
    if (this._presenceTimer.unref) this._presenceTimer.unref()

    await this._loadOutbox()
    return this
  }

  async close () {
    if (this.closing) return this.closing
    this.closing = (async () => {
      this.closed = true
      if (this._admissionTimer) clearInterval(this._admissionTimer)
      if (this._typingTimer) clearInterval(this._typingTimer)
      if (this._presenceTimer) clearInterval(this._presenceTimer)
      if (this._detach) this._detach()
      if (this.bus) this.bus.destroy()
      if (this.base) await this.client.transport.leave(this.base.discoveryKey).catch(() => {})
      if (this.blobs) await this.blobs.close().catch(() => {})
      if (this.base) await this.base.close().catch(() => {})
      this.removeAllListeners()
    })()
    return this.closing
  }

  /* ------------------------------------------------------------------ */
  /* identity of this room                                              */
  /* ------------------------------------------------------------------ */

  get id () {
    return this.localId
  }

  get discoveryKey () {
    return this.base ? this.base.discoveryKey : null
  }

  get writable () {
    return !!this.base && this.base.writable
  }

  /** Whether a message sent now waits in the outbox rather than failing. */
  get queueing () {
    return !this.writable && this.wantsAdmission
  }

  get writerKey () {
    return this.base && this.base.local ? this.base.local.key : null
  }

  get me () {
    return this.client.identity.id
  }

  async meta () {
    return this.view.meta()
  }

  async myRole () {
    return this.view.role(this.me)
  }

  async can (action) {
    const [meta, role, memberCount] = await Promise.all([
      this.view.meta(),
      this.myRole(),
      this.view.memberCount()
    ])
    if (!meta || role === null) return false
    return can(action, role, meta.type, meta.settings, { memberCount })
  }

  /* ------------------------------------------------------------------ */
  /* writing                                                            */
  /* ------------------------------------------------------------------ */

  async _append (type, payload, opts = {}) {
    if (!this.base.writable) {
      throw new Error('You do not have write access to this room yet')
    }
    if (opts.timestamp === undefined) opts = { ...opts, timestamp: await this._nextTimestamp() }
    const op = createOp(type, payload, this.client.identity, opts)
    await this.base.append(encodeOp(op))
    // Something others should see went out; wake whoever is not here to see
    // it arrive. Throttled and filtered in the client.
    if (type === OP.MESSAGE && this.client._wakeForMessage) {
      this.client._wakeForMessage(this).catch(() => {})
    }
    return z32.encode(op.id)
  }

  /**
   * The time to stamp the next operation with: now, or just after the newest
   * message this device has already seen in the room, whichever is later.
   *
   * The feed is ordered by the senders' clocks (see apply's nextTimestamp),
   * and two devices' clocks are never quite the same. A bot answering within
   * a second, on a machine whose clock ran a little behind the phone that
   * asked, stamped its answer *before* the question — and the answer showed
   * above it.
   *
   * But the stamp is also the time the message shows, so following the room
   * is kept to a few seconds. 0.2.1 followed it by up to two minutes: one
   * machine with a fast clock — a bot's, often — and every message written
   * after its answer showed a time minutes in the future. An answer to a
   * particular message says so instead (send's `after`) and follows that
   * message alone; everything else is the sender's own clock, give or take
   * the few seconds that keep a quick reply below what it replies to.
   *
   * "Own clock" is the server's time as this device last measured it (see
   * clock.js), so a phone set to the wrong zone stamps the same moment as
   * everybody else.
   */
  async _nextTimestamp ({ after = 0 } = {}) {
    const now = clockNow()
    if (after) return Math.max(now, Math.min(after + 1, now + ANSWER_FOLLOW_MAX))
    let latest = 0
    try { latest = await this.view.clock() } catch { /* a view not ready yet: now will do */ }
    return Math.max(now, Math.min(latest + 1, now + CLOCK_FOLLOW_MAX))
  }

  /** Create the room: writes the bootstrap ROOM_META + WRITER_ADD pair. */
  async initialise ({ name = '', description = '', type = ROOM_TYPE.GROUP, avatar = null, settings = {} } = {}) {
    await this._append(OP.WRITER_ADD, {
      key: this.base.local.key,
      account: this.client.identity.publicKey
    })
    await this._append(OP.ROOM_META, {
      name,
      description,
      avatar,
      type,
      settings: JSON.stringify(settings)
    })
    await this._append(OP.MEMBER_ADD, {
      account: this.client.identity.publicKey,
      name: this.client.identity.profile.name || '',
      role: ROLE.OWNER,
      avatar: avatarBytes(this.client.identity.profile.avatar),
      bio: this.client.identity.profile.bio || ''
    })
    await this.base.update()
    return this
  }

  /**
   * Post a message — or, on a device the room has not let in yet, keep it
   * and post it the moment it is (see seekAdmission). Resolves with the
   * message id either way; for a kept one, once it has actually gone.
   */
  async send (message = {}) {
    const { localKey = null, ...rest } = message
    if (!this.writable && this.wantsAdmission) return this._enqueue(rest, localKey)
    return this._sendNow(rest)
  }

  async _sendNow ({
    text = '',
    kind = KIND.TEXT,
    attachments = [],
    entities = [],
    replyTo = null,
    threadRoot = null,
    forwardFrom = null,
    forwardName = '',
    ttl = 0,
    extra = null,
    after = 0 // the time of the message this answers: stamped just after it
  } = {}) {
    const id = await this._append(OP.MESSAGE, {
      kind,
      text,
      entities,
      attachments,
      replyTo: replyTo ? toBuf(replyTo) : null,
      threadRoot: threadRoot ? toBuf(threadRoot) : null,
      forwardFrom: forwardFrom ? toBuf(forwardFrom) : null,
      forwardName,
      ttl,
      extra: extra ? JSON.stringify(extra) : ''
    }, after ? { timestamp: await this._nextTimestamp({ after }) } : {})
    this.sendTyping(false)
    return id
  }

  async sendText (text, opts = {}) {
    return this.send({ ...opts, text, kind: KIND.TEXT })
  }

  /**
   * Upload a file into the room's blob store and post it. The blob is
   * encrypted with a fresh per-file key that only travels inside the message.
   */
  async sendFile (file, { kind = null, text = '', onProgress = null, ...rest } = {}) {
    const meta = await describeFile(file)
    const { blobId, core, key, size } = await this.blobs.put(
      file.data || file, { onProgress }
    )

    const attachment = {
      blobId,
      core,
      key,
      name: meta.name,
      mime: meta.mime,
      size,
      width: meta.width || 0,
      height: meta.height || 0,
      duration: meta.duration || 0,
      thumb: meta.thumb || null,
      blurhash: meta.blurhash || '',
      waveform: meta.waveform || null
    }

    return this.send({
      ...rest,
      text,
      kind: kind ?? kindForMime(meta.mime),
      attachments: [attachment]
    })
  }

  async downloadAttachment (attachment, opts) {
    return this.blobs.get(attachment, opts)
  }

  async edit (messageId, text, entities = []) {
    return this._append(OP.EDIT, { target: toBuf(messageId), text, entities })
  }

  async remove (messageId, { forEveryone = true } = {}) {
    return this._append(OP.DELETE, {
      target: toBuf(messageId),
      scope: forEveryone ? DELETE_SCOPE.EVERYONE : DELETE_SCOPE.ME
    })
  }

  async react (messageId, emoji, { remove = false } = {}) {
    return this._append(OP.REACTION, { target: toBuf(messageId), emoji, remove })
  }

  async toggleReaction (messageId, emoji) {
    const existing = await this.view.reactions(messageId)
    const mine = existing.find((r) => r.emoji === emoji && r.accounts.includes(this.me))
    return this.react(messageId, emoji, { remove: !!mine })
  }

  async pin (messageId, { remove = false } = {}) {
    return this._append(OP.PIN, { target: toBuf(messageId), remove })
  }

  async markRead (messageId = null) {
    const last = messageId ? await this.view.message(messageId) : await this.view.lastMessage()
    if (!last) return null
    return this._append(OP.READ, { upTo: toBuf(last.id), at: last.timestamp })
  }

  async createPoll ({ question, options, multiple = false, anonymous = false, closesAt = 0 }) {
    return this.send({
      kind: KIND.POLL,
      text: question,
      extra: {
        question,
        options,
        flags: (multiple ? 1 : 0) | (anonymous ? 2 : 0),
        closesAt
      }
    })
  }

  async vote (pollId, options) {
    return this._append(OP.POLL_VOTE, {
      poll: toBuf(pollId),
      options: Array.isArray(options) ? options : [options]
    })
  }

  async setMeta (patch) {
    const meta = (await this.view.meta()) || {}
    return this._append(OP.ROOM_META, {
      name: patch.name ?? meta.name ?? '',
      description: patch.description ?? meta.description ?? '',
      avatar: patch.avatar ?? null,
      type: meta.type ?? ROOM_TYPE.GROUP,
      settings: JSON.stringify({ ...normalizeSettings(meta.settings), ...(patch.settings || {}) })
    })
  }

  async setRole (account, role) {
    return this._append(OP.MEMBER_ROLE, { account: toBuf(account), role })
  }

  async kick (account) {
    return this._append(OP.MEMBER_REMOVE, { account: toBuf(account) })
  }

  async leave () {
    if (this.base.writable) {
      await this._append(OP.MEMBER_REMOVE, { account: this.client.identity.publicKey })
    }
    await this.close()
  }

  /** Authorise a joiner's writer core. Called by the pairing host. */
  async addWriter (writerKey, account, { name = '', role = ROLE.MEMBER } = {}) {
    await this._append(OP.WRITER_ADD, { key: toBuf(writerKey), account: toBuf(account) })
    await this._append(OP.MEMBER_ADD, {
      account: toBuf(account),
      name,
      role,
      avatar: null,
      bio: ''
    })
    await this.base.update()
  }

  /**
   * Say who we are to everyone connected right now.
   *
   * announceSelf() below is the durable half: it writes a record that the
   * room keeps and that reaches members who are asleep today and open the app
   * on Thursday. This is the immediate half — an unstored frame down the same
   * signed bus that carries typing and presence, so a name or a photo changed
   * on one phone appears on the other one in the next second rather than
   * whenever the append happens to replicate.
   *
   * Both are needed. Alone, this reaches nobody who is offline; alone, the
   * append can sit unseen while both people are staring at the chat.
   */
  announceProfile ({ withAvatar = true, to = null } = {}) {
    if (!this.bus) return
    const profile = this.client.identity.profile || {}
    this.bus.send(EPH.PROFILE, {
      name: profile.name || '',
      // A photo is up to ~96 KB and goes to every peer in every room, so it
      // rides along only when it is the thing that changed. Renaming yourself
      // should not re-send your face to everybody you have ever spoken to.
      avatar: withAvatar ? (profile.avatar || null) : undefined,
      bio: profile.bio || '',
      username: profile.username || '',
      // A bot's card: what it does and which commands it answers. Ordinary
      // accounts have none; a client from before bots ignores the field.
      bot: profile.bot || undefined,
      at: profile.updatedAt || Date.now()
    }, { to })
  }

  /** Announce our own profile inside the room once we become writable. */
  async announceSelf () {
    if (!this.base.writable) return false
    const existing = await this.view.member(this.me)
    const profile = this.client.identity.profile
    // Re-announce when any shown field changed, not only the name — an
    // avatar-only change used to stay invisible to everyone else.
    if (existing &&
        existing.name === (profile.name || '') &&
        (existing.avatar || null) === (profile.avatar || null) &&
        (existing.bio || '') === (profile.bio || '')) {
      return false
    }
    await this._append(OP.MEMBER_ADD, {
      account: this.client.identity.publicKey,
      name: profile.name || '',
      role: ROLE.MEMBER,
      avatar: avatarBytes(profile.avatar),
      bio: profile.bio || ''
    })
    return true
  }

  async logCall ({ callId, state, video, startedAt, duration, participants }) {
    return this._append(OP.CALL_LOG, {
      callId: toBuf(callId),
      state,
      video: !!video,
      startedAt: startedAt || clockNow(),
      duration: duration || 0,
      participants: (participants || []).map(toBuf)
    })
  }

  /**
   * Block until this device has write access. After pairing there is a gap
   * while the host's WRITER_ADD op replicates back to us.
   */
  async waitForWritable ({ timeout = 60000 } = {}) {
    if (this.base.writable) return true
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.base.off('update', check)
        resolve(false)
      }, timeout)

      const check = () => {
        if (!this.base.writable) return
        clearTimeout(timer)
        this.base.off('update', check)
        resolve(true)
      }
      this.base.on('update', check)
      check()
    })
  }

  /* ------------------------------------------------------------------ */
  /* live signalling                                                    */
  /* ------------------------------------------------------------------ */

  sendTyping (active = true) {
    if (!this.bus) return
    this.bus.send(EPH.TYPING, { active, until: Date.now() + TYPING_TTL })
  }

  announcePresence (state = 'online') {
    if (!this.bus) return
    this.bus.send(EPH.PRESENCE, { state })
  }

  sendCallSignal (payload, { to = null } = {}) {
    if (!this.bus) return
    this.bus.send(EPH.CALL, payload, { to })
  }

  get onlineCount () {
    let n = 0
    const now = Date.now()
    for (const p of this.presence.values()) if (now - p.at < PRESENCE_INTERVAL * 2.5) n++
    return n
  }

  get typingAccounts () {
    const now = Date.now()
    const out = []
    for (const [account, until] of this.typing) if (until > now) out.push(account)
    return out
  }

  /** When this member was last heard from in this chat, by anything they sent. */
  lastSeen (account) {
    return this.seen.get(account) || 0
  }

  _onEphemeral (msg) {
    if (msg.account === this.me) return
    // Any signed frame is proof of life: typing, a receipt, a profile card.
    if (msg.at > (this.seen.get(msg.account) || 0)) this.seen.set(msg.account, Math.min(msg.at, Date.now()))

    switch (msg.type) {
      case EPH.TYPING: {
        if (msg.body.active) this.typing.set(msg.account, msg.body.until || Date.now() + TYPING_TTL)
        else this.typing.delete(msg.account)
        this._scheduleTypingSweep()
        this.emit('typing', this.typingAccounts)
        break
      }
      case EPH.PRESENCE: {
        this.presence.set(msg.account, { state: msg.body.state || 'online', at: msg.at })
        this.emit('presence', msg.account, this.presence.get(msg.account))
        break
      }
      case EPH.RECEIPT: {
        this.emit('receipt', msg.account, msg.body)
        break
      }
      case EPH.CALL: {
        this.emit('call-signal', { ...msg.body, from: msg.account, peer: msg.from })
        break
      }
      case EPH.PROFILE: {
        this.emit('peer-profile', msg.account, msg.body)
        break
      }
      case EPH.ADMIT: {
        this._onAdmit(msg).catch(() => {})
        break
      }
    }
  }

  _scheduleTypingSweep () {
    if (this._typingTimer) return
    this._typingTimer = setInterval(() => {
      const now = Date.now()
      let changed = false
      for (const [account, until] of this.typing) {
        if (until <= now) {
          this.typing.delete(account)
          changed = true
        }
      }
      if (changed) this.emit('typing', this.typingAccounts)
      if (this.typing.size === 0) {
        clearInterval(this._typingTimer)
        this._typingTimer = null
      }
    }, 1000)
    if (this._typingTimer.unref) this._typingTimer.unref()
  }

  async _onUpdate () {
    if (this.closed) return
    if (this.wantsAdmission && this.writable) {
      this.wantsAdmission = false
      if (this._admissionTimer) clearInterval(this._admissionTimer)
      this._admissionTimer = null
      this.emit('admitted')
    }
    if (this.writable && this.outbox.length) this._flushOutbox().catch(() => {})
    this.emit('update')
    try {
      const meta = await this.view.meta()
      const stamp = meta ? JSON.stringify([meta.name, meta.description, meta.avatar]) : null
      if (stamp !== this._lastMeta) {
        this._lastMeta = stamp
        this.emit('meta', meta)
      }
    } catch { /* view closed mid-update */ }
  }
}

/* ---------------- letting a device in ---------------- */

/*
 * A device of a member that the room does not take writes from yet: an
 * account restored onto a new phone, mostly. It has the room's keys (the
 * vault gave them back) and reads it, and is connected to whoever else is
 * in it, but its writer core is one nobody has authorised.
 *
 * It used to get in by asking every member over their inboxes for a fresh
 * invite, waiting on a sealed address for one to come back, and redeeming
 * it over a pairing connection of its own: three exchanges, each needing
 * somebody online at that instant, and a failure anywhere left the chat
 * read-only with "waiting for access" under it however often it asked.
 * Now it asks the room itself, over the connections it already has: a
 * frame signed with the account key, naming the writer core. Any member
 * device that may let people in (the same rule apply uses) checks that the
 * account is a member and adds the core. That grants nothing new: the
 * account is in the room already, under its own key.
 *
 * Meanwhile the chat can be written in. What is sent waits in an outbox,
 * kept on disk, and goes out in order the moment the room takes writes
 * from this device.
 */
const ADMISSION_EVERY = 15 * 1000
const ADMIT_AGAIN_AFTER = 20 * 1000
const OUTBOX_TRIES = 3

Object.assign(Room.prototype, {
  /** Start asking to be let in, and keep asking until we are. */
  seekAdmission ({ every = ADMISSION_EVERY } = {}) {
    if (this.closed || !this.base || this.writable) return false
    this.wantsAdmission = true
    this.requestAdmission()
    if (!this._admissionTimer) {
      this._admissionTimer = setInterval(() => {
        if (this.closed || this.writable) {
          clearInterval(this._admissionTimer)
          this._admissionTimer = null
          return
        }
        this.requestAdmission()
      }, every)
      if (this._admissionTimer.unref) this._admissionTimer.unref()
    }
    this.emit('outbox')
    return true
  },

  requestAdmission ({ to = null } = {}) {
    if (this.closed || !this.bus || !this.base || this.writable || !this.writerKey) return false
    this.bus.send(EPH.ADMIT, { writer: b4a.toString(this.writerKey, 'hex') }, { to })
    return true
  },

  async _onAdmit (msg) {
    if (!this.writable || !msg.body || typeof msg.body.writer !== 'string') return
    const hex = msg.body.writer.toLowerCase()
    if (!/^[0-9a-f]{64}$/.test(hex)) return
    // Asked again before our answer reached them: once is enough.
    if (Date.now() - (this._admitted.get(hex) || 0) < ADMIT_AGAIN_AFTER) return

    const [meta, member, mine, writers] = await Promise.all([
      this.view.meta(), this.view.member(msg.account), this.view.member(this.me), this.view.writers()
    ])
    if (!meta || !member || !mine) return
    // The rule apply holds the op to; anything else would be dropped there.
    const mayAdmit = can(ACTION.ADD_WRITER, mine.role, meta.type, meta.settings) ||
      can(ACTION.INVITE, mine.role, meta.type, meta.settings)
    if (!mayAdmit) return
    const record = writers.find((w) => w && w.account === msg.account)
    const keys = record ? (Array.isArray(record.keys) ? record.keys : [record.key]) : []
    if (keys.includes(hex)) return

    this._admitted.set(hex, Date.now())
    await this._append(OP.WRITER_ADD, { key: b4a.from(hex, 'hex'), account: msg.accountKey })
    await this.base.update()
    this.emit('admitted-device', { account: msg.account })
  },

  /** What waits to be sent, for the screen: { key, text, files, at }. */
  queuedMessages () {
    return this.outbox.map((entry) => ({
      key: entry.key,
      text: entry.args.text || '',
      files: (entry.args.attachments || []).map((a) => a.name || ''),
      at: entry.at
    }))
  },

  _enqueue (args, localKey) {
    const entry = {
      key: localKey || 'out-' + b4a.toString(randomBytes(8), 'hex'),
      args,
      at: clockNow(),
      tries: 0
    }
    this.outbox.push(entry)
    const done = new Promise((resolve, reject) => this._outboxWaiters.set(entry.key, { resolve, reject }))
    this._saveOutbox().catch(() => {})
    this.emit('outbox')
    // Let in while this was being written down.
    if (this.writable) this._flushOutbox().catch(() => {})
    return done
  },

  async _flushOutbox () {
    if (this._flushing) return
    this._flushing = true
    try {
      while (this.outbox.length && this.writable && !this.closed) {
        const entry = this.outbox[0]
        let id = null
        let failed = null
        try {
          id = await this._sendNow(entry.args)
        } catch (err) {
          failed = err
        }
        if (failed && ++entry.tries < OUTBOX_TRIES) break
        this.outbox.shift()
        await this._saveOutbox().catch(() => {})
        const waiter = this._outboxWaiters.get(entry.key)
        this._outboxWaiters.delete(entry.key)
        if (waiter) failed ? waiter.reject(failed) : waiter.resolve(id)
        this.emit('outbox')
      }
    } finally {
      this._flushing = false
    }
  },

  _outboxKey () {
    return 'outbox-room/' + this.localId
  },

  async _saveOutbox () {
    const local = this.client && this.client.local
    if (!local || !this.localId) return
    if (!this.outbox.length) return local.del(this._outboxKey())
    await local.put(this._outboxKey(), {
      entries: this.outbox.map((e) => ({ key: e.key, at: e.at, tries: e.tries, args: pack(e.args) }))
    })
  },

  /** What was written before the app last closed, still waiting. */
  async _loadOutbox () {
    const local = this.client && this.client.local
    if (!local || !this.localId) return
    const node = await local.get(this._outboxKey()).catch(() => null)
    const entries = node && node.value && Array.isArray(node.value.entries) ? node.value.entries : []
    this.outbox = entries.map((e) => ({ key: e.key, at: e.at, tries: e.tries || 0, args: unpack(e.args) }))
    if (this.outbox.length && this.writable) this._flushOutbox().catch(() => {})
  }
})

/* Bytes survive the trip through the JSON store as hex. */
function pack (value) {
  if (b4a.isBuffer(value) || value instanceof Uint8Array) return { $bytes: b4a.toString(value, 'hex') }
  if (Array.isArray(value)) return value.map(pack)
  if (value && typeof value === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(value)) out[k] = pack(v)
    return out
  }
  return value
}

function unpack (value) {
  if (value && typeof value === 'object' && typeof value.$bytes === 'string') return b4a.from(value.$bytes, 'hex')
  if (Array.isArray(value)) return value.map(unpack)
  if (value && typeof value === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(value)) out[k] = unpack(v)
    return out
  }
  return value
}

/* ---------------- helpers ---------------- */

function toBuf (value) {
  if (value == null) return null
  if (typeof value === 'string') {
    return /^[0-9a-f]{64}$/i.test(value) ? b4a.from(value, 'hex') : z32.decode(value)
  }
  return value
}

/**
 * An avatar travels as the UTF-8 bytes of its data URI, so the field that the
 * protocol carries as an optional buffer holds a string the UI can drop
 * straight into <img src>. null when there is no avatar.
 */
function avatarBytes (avatar) {
  return avatar ? b4a.from(String(avatar), 'utf8') : null
}

export function kindForMime (mime = '') {
  if (mime.startsWith('image/gif')) return KIND.GIF
  if (mime.startsWith('image/')) return KIND.IMAGE
  if (mime.startsWith('video/')) return KIND.VIDEO
  if (mime.startsWith('audio/')) return KIND.AUDIO
  return KIND.FILE
}

async function describeFile (file) {
  if (file && file.meta) return file.meta
  return {
    name: file.name || 'file',
    mime: file.type || file.mime || 'application/octet-stream',
    width: file.width || 0,
    height: file.height || 0,
    duration: file.duration || 0,
    thumb: file.thumb || null,
    blurhash: file.blurhash || '',
    waveform: file.waveform || null
  }
}

export { ROOM_TYPE, ROLE, KIND, CALL_STATE, ACTION, randomBytes }
